import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { parsePhoneNumberFromString, AsYouType } from "libphonenumber-js/min";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { QuickOrderHeader } from "../components/QuickOrderHeader";
import { QOHoneypot, QO_LOAD_KEY } from "../components/QOHoneypot";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({ baseURL: `${BACKEND}/api`, timeout: 20000 });

/* v85.48 — Halaman publik /quick-order-public
 *   Satu input nomor HP + tombol Lanjut. Setelah submit:
 *     • Backend normalisasi via `to_local_phone` (handle 62xx / +62 / 08xx)
 *     • Backend lookup/create token aktif → return token
 *     • Frontend window.location = /order/{token}
 *   Mobile-first, tanpa chrome aplikasi (sidebar, header, dsb).
 *   Jika nomor sudah ada di customers / work_orders, alamat historis otomatis
 *   terisi karena halaman /order/:token sudah mengambil alamat dari history.
 *
 *   PENTING: App ini dark-only (lihat index.css @layer utilities). Semua Tailwind
 *   semantic classes (bg-white, text-zinc-900, dst) di-override `!important` ke
 *   palette dark. Supaya halaman publik ini tetap TERANG, gunakan literal hex
 *   `bg-[#...]` / `text-[#...]` yang tidak di-match selector override.
 */
export default function QuickOrderPublic() {
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  useEffect(() => { sessionStorage.setItem(QO_LOAD_KEY, String(Date.now())); }, []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [leaving, setLeaving] = useState(false);
  const navigate = useNavigate();

  // v85.54 — Validasi nomor pakai libphonenumber-js (JS port Google libphonenumber).
  // Default region "ID". Hanya nomor MOBILE Indonesia yg valid (national prefix "8",
  // panjang 10-13 digit setelah strip 0) → tombol Lanjut aktif. Lebih ketat drpd
  // `isValid()` saja: fixed-line (021/022/dll) ditolak, nomor < 10 digit juga ditolak.
  const validation = useMemo(() => {
    const raw = (phone || "").trim();
    if (!raw) return { valid: false, type: "", formatted: "", reason: "" };
    try {
      const pn = parsePhoneNumberFromString(raw, "ID");
      if (!pn) return { valid: false, type: "", formatted: "", reason: "Format nomor belum lengkap" };
      if (pn.country !== "ID") {
        return { valid: false, type: "", formatted: raw, reason: "Hanya nomor Indonesia yg didukung" };
      }
      if (!pn.isValid()) {
        return { valid: false, type: "", formatted: pn.formatNational?.() || raw, reason: "Nomor belum lengkap" };
      }
      // Enforce MOBILE: national significant number harus mulai dgn "8"
      // (semua operator seluler ID: Telkomsel 811-813, Indosat 814-816, XL 817-819,
      //  Smartfren 881-889, Tri 895-899, dsb — semua prefix 8).
      const national = (pn.nationalNumber || "").toString();
      if (!national.startsWith("8")) {
        return { valid: false, type: pn.getType() || "FIXED_LINE", formatted: pn.formatNational(),
                 reason: "Nomor bukan HP seluler (gunakan nomor 08xx)" };
      }
      // Panjang nasional minimal 10 digit (termasuk prefix 8)
      if (national.length < 10 || national.length > 13) {
        return { valid: false, type: pn.getType() || "MOBILE", formatted: pn.formatNational(),
                 reason: national.length < 10 ? "Nomor belum lengkap" : "Nomor terlalu panjang" };
      }
      return {
        valid: true,
        type: pn.getType() || "MOBILE",
        formatted: pn.formatNational(),
        reason: "",
      };
    } catch {
      return { valid: false, type: "", formatted: raw, reason: "Format nomor belum lengkap" };
    }
  }, [phone]);

  // Override global `color-scheme: dark` di body supaya native form controls
  // (input, button) render dgn palette terang di halaman publik ini.
  useEffect(() => {
    const prevScheme = document.body.style.colorScheme;
    document.body.style.colorScheme = "light";
    return () => { document.body.style.colorScheme = prevScheme; };
  }, []);

  async function submit(e) {
    e?.preventDefault?.();
    if (!validation.valid) { setErr(validation.reason || "Nomor HP tidak valid"); return; }
    setErr("");
    setBusy(true);
    try {
      const r = await API.post("/public/order-token/by-phone", { phone, website });
      const tok = r?.data?.token;
      if (!tok) throw new Error("Token tidak diterima");
      setLeaving(true);
      await new Promise(r => setTimeout(r, 280));
      navigate(`/order/${tok}`, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Terjadi kesalahan";
      setErr(msg);
      setBusy(false);
    }
  }

  // v85.54 — format input live dgn AsYouType ("ID") supaya user baca lebih enak.
  function onPhoneChange(e) {
    const raw = e.target.value;
    if (err) setErr("");
    // Buang karakter non-digit kecuali '+' di depan, lalu re-format
    const cleaned = raw.replace(/[^\d+]/g, "");
    try {
      const formatted = new AsYouType("ID").input(cleaned);
      setPhone(formatted);
    } catch {
      setPhone(cleaned);
    }
  }

  const canSubmit = validation.valid && !busy;

  return (
    <div
      className="min-h-screen w-full flex flex-col"
      style={{ colorScheme: "dark", backgroundColor: "#1C1C1E" }}
      data-testid="quick-order-public-page"
    >
      <QuickOrderHeader />

      <div className="flex-1 flex items-start justify-center p-5 pt-10 overflow-hidden">
      <motion.div
        className="w-full max-w-sm"
        initial={{ x: -40, opacity: 0 }}
        animate={leaving ? { x: "-110%", opacity: 0 } : { x: 0, opacity: 1 }}
        transition={leaving ? { duration: 0.28, ease: "easeIn" } : { type: "spring", stiffness: 260, damping: 30 }}
        data-testid="qop-content"
      >
        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E0662A] text-white mb-3 shadow-md">
            <Phone className="w-7 h-7" />
          </div>
          <h1
            className="font-['Manrope'] font-bold text-[22px] tracking-tight leading-tight"
            style={{ color: "#FFFFFF" }}
          >
            Pesan Service AC
          </h1>
          <p className="text-[13px] mt-1 leading-snug" style={{ color: "#A1A1AA" }}>
            Masukkan nomor WhatsApp Anda untuk memulai pemesanan
          </p>
        </div>

        {/* Form card */}
        <form
          onSubmit={submit}
          className="rounded-sm p-5 space-y-4"
          style={{
            backgroundColor: "#1C1C1E",
            border: "1px solid #3F3F46",
          }}
          data-testid="quick-order-public-form"
        >
          <QOHoneypot value={website} onChange={setWebsite} />
          <div>
            <label
              className="block text-[11px] uppercase tracking-widest font-semibold mb-1.5"
              style={{ color: "#A1A1AA" }}
            >
              Nomor WhatsApp
            </label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              value={phone}
              onChange={onPhoneChange}
              placeholder="08xx xxxx xxxx"
              className="w-full h-12 px-3 rounded-sm text-[16px] font-mono focus:outline-none transition-colors"
              style={{
                backgroundColor: "#141416",
                color: "#FFFFFF",
                border: `1px solid ${
                  !phone ? "#3F3F46" :
                  validation.valid ? "#4ADE80" :     // hijau = valid
                  "#F59E0B"                           // kuning = sedang mengetik / belum valid
                }`,
                colorScheme: "dark",
              }}
              data-testid="qop-phone-input"
              disabled={busy}
              onFocus={e => { e.currentTarget.style.boxShadow = "0 0 0 2px rgba(224,102,42,0.3)"; }}
              onBlur={e => { e.currentTarget.style.boxShadow = "none"; }}
            />
            {/* Live validation feedback */}
            {phone && (
              <div
                className="flex items-center gap-1.5 text-[12px] mt-1.5"
                style={{ color: validation.valid ? "#4ADE80" : "#FBBF24" }}
                data-testid="qop-validation-hint"
              >
                {validation.valid ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Nomor valid: <span className="font-mono font-semibold">{validation.formatted}</span></span>
                  </>
                ) : (
                  <span>{validation.reason || "Nomor belum lengkap"}</span>
                )}
              </div>
            )}
            {err && (
              <div className="text-[12px] mt-1.5" style={{ color: "#F87171" }} data-testid="qop-error">
                {err}
              </div>
            )}
            <div className="text-[11px] mt-1.5" style={{ color: "#A1A1AA" }}>
              Format: <span className="font-mono">08xxxxxxxxxx</span> atau <span className="font-mono">62xxxxxxxxxx</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-12 rounded-sm text-white font-bold text-[14px] flex items-center justify-center gap-2 transition-colors"
            style={{
              backgroundColor: !canSubmit ? "#3F3F46" : "#E0662A",
              color: !canSubmit ? "#A1A1AA" : "#FFFFFF",
              cursor: !canSubmit ? "not-allowed" : "pointer",
            }}
            onMouseEnter={e => { if (canSubmit) e.currentTarget.style.backgroundColor = "#F57C00"; }}
            onMouseLeave={e => { if (canSubmit) e.currentTarget.style.backgroundColor = "#E0662A"; }}
            data-testid="qop-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Memproses…
              </>
            ) : (
              <>
                Lanjut
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Trust note */}
        <div
          className="text-center text-[11px] mt-5 leading-relaxed"
          style={{ color: "#A1A1AA" }}
        >
          Nomor Anda hanya dipakai untuk konfirmasi pemesanan.<br />
          Data riwayat akan muncul otomatis bila pernah service sebelumnya.
        </div>
      </motion.div>
      </div>
    </div>
  );
}
